import { useEffect, useState } from 'react';
import { lastPageNumber, orderablePackageParams, pageTotal } from '@mypoz/shared';

import { fetchPackagePage } from '@/features/packages/api.js';

export interface OrderablePackagePage {
  /** Vendor rows on the last page answered — normalise with `usePackages().toPackages`. */
  readonly rows: readonly unknown[];
  /** How many packages match, corrected for the vendor's one-too-high `count` (`pageTotal`). */
  readonly total: number;
  /** `loading` only before the first answer; later changes keep the last page, `refreshing`. */
  readonly status: 'loading' | 'ready' | 'error';
  /**
   * The page on screen is not the one asked for yet. Worked out from which request the answer
   * belongs to, not set by the request itself, so it is already true on the very render that asks
   * for something new — no render in between treats an old answer as the new one.
   */
  readonly refreshing: boolean;
  retry: () => void;
}

/**
 * One page of the packages a new order can use, asked of the vendor (MYP-130).
 *
 * `statusCodes` is the vendor's orderable list (`package_status_for_order_list`); `undefined`
 * while it loads. An empty list asks nothing: `package_list` with no status filter would answer
 * with every package, so no codes means nothing can be ordered. `version` re-asks after the
 * package list reloads (an order submitted, say).
 */
export function useOrderablePackagePage(
  statusCodes: readonly string[] | undefined,
  search: string,
  page: number,
  size: number,
  version: number,
): OrderablePackagePage {
  const [attempt, setAttempt] = useState(0);
  const codes = statusCodes?.join(',');

  // What is being asked: the list (`scope`), and the page of it (`key`).
  const scope = `${codes ?? ''}\u0000${search}\u0000${size}\u0000${version}\u0000${attempt}`;
  const key = `${scope}\u0000${page}`;

  const [answer, setAnswer] = useState<{
    key: string;
    scope: string;
    page: number;
    rows: readonly unknown[];
    /** The vendor's own `count`, one too high. */
    count: number;
    total: number;
  }>();
  /*
    The exact total for a `scope`, from one extra request for the page the vendor's count calls the
    last (`lastPageNumber`). Without it, "All (31)" would show for 30 packages until someone paged
    to the end.
  */
  const [exact, setExact] = useState<{ scope: string; total: number }>();
  const [failedKey, setFailedKey] = useState<string>();
  /*
    The most the list can hold, learnt from an empty page past the end (`pageTotal`). Kept per
    `scope`: once page 4 of 30 packages has come back empty, page 3 — full, so the vendor's 31
    again — must not bring back the empty page 4.
  */
  const [cap, setCap] = useState<{ scope: string; max: number }>();

  useEffect(() => {
    if (codes === undefined) return;
    let current = true;

    if (codes === '') {
      setAnswer({ key, scope, page, rows: [], count: 0, total: 0 });
      return;
    }

    fetchPackagePage(orderablePackageParams(codes.split(','), search, page, size)).then(
      (result) => {
        if (!current) return;
        if (result.rows.length === 0 && page > 1) {
          setCap({ scope, max: (page - 1) * size });
        }
        setAnswer({
          key,
          scope,
          page,
          rows: result.rows,
          count: result.count,
          total: pageTotal(result.count, page, size, result.rows.length),
        });
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[orders] could not load a page of orderable packages', cause);
        setFailedKey(key);
      },
    );

    return () => {
      current = false;
    };
  }, [codes, search, page, size, key, scope]);

  useEffect(() => {
    if (codes === undefined || codes === '' || answer?.scope !== scope) return;
    if (exact?.scope === scope) return;
    const last = lastPageNumber(answer.count, size);
    // The page on screen already settles it: it is the last one, or it came back short.
    if (last <= 1 || answer.page >= last || answer.rows.length < size) {
      setExact({ scope, total: answer.total });
      return;
    }
    let current = true;
    fetchPackagePage(orderablePackageParams(codes.split(','), search, last, size)).then(
      (result) => {
        if (current)
          setExact({ scope, total: pageTotal(result.count, last, size, result.rows.length) });
      },
      (cause: unknown) => {
        // Not worth an error on screen: the vendor's count stands until the last page is reached.
        if (current)
          console.error('[orders] could not check the total of orderable packages', cause);
      },
    );
    return () => {
      current = false;
    };
  }, [codes, search, size, scope, answer, exact]);

  const capped =
    answer === undefined
      ? 0
      : cap?.scope === scope
        ? Math.min(answer.total, cap.max)
        : answer.total;

  return {
    rows: answer?.rows ?? [],
    total: exact?.scope === scope ? exact.total : capped,
    status: failedKey === key ? 'error' : answer === undefined ? 'loading' : 'ready',
    refreshing: codes !== undefined && answer !== undefined && answer.key !== key,
    retry: () => setAttempt((previous) => previous + 1),
  };
}
